import { useEffect } from "react";
import { BundleTypes } from "../apps/Bundles/constants";

const ATTACH_TO_ELEMENT_TYPES = [
    BundleTypes.BUNDLE_BUILDER,
    BundleTypes.MIX_MATCH_SECTIONAL,
    BundleTypes.BXGY,
    BundleTypes.MIX_MATCH_SECTIONAL,
    BundleTypes.FBT,
    BundleTypes.STANDARD,
    BundleTypes.PRODUCT_ADDONS,
    BundleTypes.PRODUCT_ADDONS_POPUP,
    BundleTypes.BUY_N,
    BundleTypes.BB_PLACEHOLDER,
];

function getScrollParentBounds(element: HTMLElement): DOMRect {
    let parent: HTMLElement | null = element.parentElement;

    while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;

        // If any scroll exists, consider this parent as scroll container
        if (
            overflowY === "auto" ||
            overflowY === "scroll" ||
            overflowX === "auto" ||
            overflowX === "scroll" ||
            overflowY === "hidden" ||
            overflowX === "hidden"
        ) {
            return parent.getBoundingClientRect();
        }

        parent = parent.parentElement;
    }

    return document.body.getBoundingClientRect(); // fallback to body
}

export function useOverlayTooltip(
    inspectorMode: boolean,
    element: HTMLElement | null,
    label: string | null,
    isSelected: boolean,
    previewRef: React.RefObject<HTMLDivElement>,
    bundleConfig: any,
) {
    useEffect(() => {
        if (!inspectorMode || !element || !document.body.contains(element)) return;

        element.style.boxShadow = isSelected
            ? "0 0 0 2px rgba(42, 104, 192, 1)" // selected
            : "0 0 0 1px #458FFF"; // hover

        let tooltip: HTMLDivElement | null = null;
        const attachToElement = ATTACH_TO_ELEMENT_TYPES.includes(bundleConfig?.bundle_type);

        if (isSelected && label) {
            tooltip = document.createElement("div");
            tooltip.setAttribute("data-overlay-tooltip", "true");
            tooltip.innerText = label;

            if (attachToElement) {
                // Store original styles for cleanup
                const originalPosition = element.style.position || window.getComputedStyle(element).position;

                element.appendChild(tooltip);

                Object.assign(tooltip.style, {
                    position: "absolute",
                    background: "#0070f3",
                    color: "#fff",
                    fontSize: "12px",
                    padding: "2px 6px",
                    fontWeight: "500",
                    borderRadius: "3px",
                    whiteSpace: "nowrap",
                    zIndex: "3",
                    pointerEvents: "none",
                } as Partial<CSSStyleDeclaration>);

                // Only adjust element's positioning if it's not absolute/fixed/sticky
                if (!["absolute", "fixed", "sticky"].includes(originalPosition)) {
                    element.style.position = "relative";
                }

                const containerBounds = getScrollParentBounds(element);
                const rect = element.getBoundingClientRect();
                const tooltipBounds = tooltip.getBoundingClientRect();
                const tooltipHeight = 20;
                const margin = 2;
                const tooltipWidth = tooltip.offsetWidth; // tooltip must be appended to DOM

                // --- Vertical placement (your original logic) ---
                let top: number;
                if (rect.top - containerBounds.top >= tooltipHeight + margin) {
                    top = -tooltipHeight - margin; // enough space above
                } else if (containerBounds.bottom - rect.bottom >= tooltipHeight + margin) {
                    top = rect.height + margin; // enough space below
                } else {
                    top = 0; //rect.height / 2 - tooltipHeight / 2
                }
                tooltip.style.top = `${top}px`;

                // --- Horizontal placement (independent, only check right) ---
                const spaceRight = containerBounds.right - tooltipBounds.right;
                let left: number;

                if (spaceRight >= tooltipWidth + margin) {
                    left = 0; // enough space on right → start from left edge
                } else {
                    left = rect.width - tooltipWidth; // not enough → align right edge
                }
                tooltip.style.left = `${left}px`;

                // Cleanup function: remove tooltip and restore original position
                return () => {
                    tooltip?.remove();
                    element.style.position = originalPosition;
                    element.style.boxShadow = "";
                };
            } else {
                // --- OLD BEHAVIOR: attach to previewRef ---
                if (!previewRef.current) return;

                if (getComputedStyle(previewRef.current).position === "static") {
                    previewRef.current.style.position = "relative";
                }

                previewRef.current.appendChild(tooltip);

                Object.assign(tooltip.style, {
                    position: "absolute",
                    background: "#0070f3",
                    color: "#fff",
                    fontSize: "12px",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                    zIndex: "10",
                    pointerEvents: "none",
                } as Partial<CSSStyleDeclaration>);

                const updatePosition = () => {
                    if (!tooltip || !previewRef.current) return;

                    const rect = element.getBoundingClientRect();
                    const containerRect = previewRef.current.getBoundingClientRect();

                    const top = rect.top - containerRect.top - 22;
                    const left = rect.left - containerRect.left;

                    tooltip.style.top = `${top}px`;
                    tooltip.style.left = `${left}px`;
                };

                window.addEventListener("resize", updatePosition);
                window.addEventListener("scroll", updatePosition, true);

                const resizeObserver = new ResizeObserver(() => updatePosition());
                resizeObserver.observe(previewRef.current);

                updatePosition();

                return () => {
                    tooltip?.remove();
                    window.removeEventListener("resize", updatePosition);
                    window.removeEventListener("scroll", updatePosition, true);
                    resizeObserver.disconnect();
                    element.style.boxShadow = "";
                };
            }
        }

        return () => {
            element.style.boxShadow = "";
        };
    }, [element, label, isSelected, inspectorMode, previewRef, bundleConfig]);
}
